import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { IResDataElasticDefault, IResponseApi } from '@core/models';
import { ApiService } from '@core/services/api.service';
import { uriConsts } from '@shared/constants';
import {
  IAddBlockParams,
  IAddBotParams,
  IAddCardParams,
  IAddGroupParams,
  IAddLogCampaignParams,
  IBotMutationData,
  ICampaignItem,
  ICampaignLogItem,
  ICampaignUserCountData,
  ICampaignUserItem,
  ICloneBlockGroupData,
  ICloneBlockGroupParams,
  IGetVoucherListResponse,
  IItemData,
  IListBotParams,
  IListBotResponse,
  IListCampaignLogsParams,
  IListCampaignParams,
  IListCampaignUserParams,
  IListCommentResponse,
  IListGroupData,
  ILoadChatbotResponse,
  IPostManageItem,
  IPostManageParams,
  IScriptApiTestResponse,
  IScriptMessageItem,
  ISendMessageCampaignData,
  ISendMessageCampaignParams,
  ISendScriptToMetaData,
  ISendScriptToMetaParams,
  ISuccessData,
  TCampaignUserExportItem,
  IUpdateCardData,
  IUpdateCardParams,
  IUpdateCampaignUserParams,
  IUpdateCommentResponse,
  IUpdateMessageData,
} from '@shared/models/chatbot/script/chatbot-api.model';
import {
  IAddChaseCampaignParams,
  IAddChaseCampaignData,
  IAddChaseScheduleParams,
  IChaseCampaignListData,
  IChaseCampaignMutationData,
  IChaseCampaign,
  IChaseCountSentData,
  IChaseScheduleApiItem,
  IChaseScheduleListData,
  IDeleteChaseImportLogsData,
  IDeleteChaseImportLogsParams,
  IGetChaseCampaignParams,
  IImportChaseLogsData,
  IImportChaseLogsParams,
  ICountSentChaseMessagesParams,
  IChaseReportData,
  IChaseReportParams,
  IListChaseCampaignParams,
  IListChaseScheduleParams,
  IRemoveChaseCampaignParams,
  IRemoveChaseScheduleParams,
  IUpdateChaseCampaignParams,
  IUpdateChaseScheduleParams,
  IViewChaseQueueData,
  IViewChaseQueueParams,
} from '@shared/models/chatbot/script/chase-script.model';
import { BotBlock, BotCard, BotGroup } from '@shared/models/chatbot/script/chatbot.model';

@Injectable({ providedIn: 'root' })
export class ChatbotScriptApiService extends ApiService {
  // ------------------------------ CHASE SCRIPT ------------------------------

  listChaseCampaign = (params: IListChaseCampaignParams) =>
    this.postRequest<IResponseApi<IChaseCampaignListData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_LIST_CAMPAIGN,
      params,
    ).pipe(
      map(response => ({
        ...response,
        data: {
          ...response.data,
          next: typeof response.data?.next === 'string' && response.data.next.trim()
            ? response.data.next
            : null,
        },
      })),
    );

  addChaseCampaign = (params: IAddChaseCampaignParams) =>
    this.postJsonRequest<IResponseApi<IAddChaseCampaignData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_ADD_CAMPAIGN,
      params,
    );

  updateChaseCampaign = (params: IUpdateChaseCampaignParams) =>
    this.postJsonRequest<IResponseApi<IChaseCampaignMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_UPDATE_CAMPAIGN,
      params,
    );

  removeChaseCampaign = (params: IRemoveChaseCampaignParams) =>
    this.postRequest<IResponseApi<IChaseCampaignMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_REMOVE_CAMPAIGN,
      params,
    );

  getChaseCampaign = (params: IGetChaseCampaignParams) =>
    this.postRequest<IResponseApi<IChaseCampaign>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_GET_CAMPAIGN,
      params,
    );

  listChaseSchedules = (params: IListChaseScheduleParams) =>
    this.postRequest<IResponseApi<IChaseScheduleListData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_LIST_SCHEDULE,
      params,
    ).pipe(
      map(response => ({
        ...response,
        data: {
          ...response.data,
          next: typeof response.data?.next === 'string' && response.data.next.trim()
            ? response.data.next
            : null,
        },
      })),
    );

  addChaseSchedule = (params: IAddChaseScheduleParams) =>
    this.postRequest<IResponseApi<IChaseScheduleApiItem>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_ADD_SCHEDULE,
      params,
    );

  updateChaseSchedule = (params: IUpdateChaseScheduleParams) =>
    this.postRequest<IResponseApi<IChaseCampaignMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_UPDATE_SCHEDULE,
      params,
    );

  removeChaseSchedule = (params: IRemoveChaseScheduleParams) =>
    this.postRequest<IResponseApi<IChaseCampaignMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_REMOVE_SCHEDULE,
      params,
    );

  reportChaseSchedule = (params: IChaseReportParams) =>
    this.postRequest<IResponseApi<IChaseReportData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_REPORT_SCHEDULE,
      params,
    );

  countSentChaseMessages = (params: ICountSentChaseMessagesParams) =>
    this.postRequest<IResponseApi<IChaseCountSentData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_COUNT_SENT_MSG,
      params,
    );

  importChaseLogs = (params: IImportChaseLogsParams) =>
    this.postRequest<IResponseApi<IImportChaseLogsData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_IMPORT_LOGS,
      params,
    );

  deleteChaseImportLogs = (params: IDeleteChaseImportLogsParams) =>
    this.postRequest<IResponseApi<IDeleteChaseImportLogsData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_DELETE_IMPORT_LOGS,
      params,
    );

  viewChaseQueue = (params: IViewChaseQueueParams) =>
    this.postRequest<IResponseApi<IViewChaseQueueData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_SCHEDULE_VIEW_LOG_QUEUE,
      params,
    );

  // ───────────────────────────── BOT ─────────────────────────────

  /**
   * Lấy danh sách bot của business.
   * V2: POST /chatbot/script/list
   */
  listBot = (params: IListBotParams) =>
    this.postRequest<IResponseApi<IListBotResponse>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_LIST,
      { size: 50, ...params },
    );

  /**
   * Lấy thông tin page theo danh sách id.
   * V2: POST /business/page/getpageinfo
   */
  getPageInfo = (params: { id: string | string[] }) =>
    this.postRequest<IResponseApi<any>>(
      uriConsts.business.PAGE_GET_INFO,
      params,
    );

  /**
   * Nạp chi tiết 1 bot (item + groups + blocks + cards).
   * V2: POST /chatbot/script/loadbot
   */
  loadChatbot = (params: { id: string }) =>
    this.postRequest<IResponseApi<ILoadChatbotResponse>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_LOAD_BOT,
      params,
    );

  /**
   * Thêm mới bot.
   * V2: POST /chatbot/script/addbot
   */
  addBot = (params: IAddBotParams) =>
    this.postRequest<IResponseApi<IBotMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_ADD_BOT,
      params,
    );

  /**
   * Cập nhật bot (botData là JSON.stringify của params thật).
   * V2: POST /chatbot/script/updatebot
   */
  updateBot = (params: { botData: string }) =>
    this.postRequest<IResponseApi<IBotMutationData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_UPDATE_BOT,
      params,
    );

  /**
   * Xoá 1 bot.
   * V2: POST /chatbot/script/removebot
   */
  removeBot = (params: { id: string; fanpageId?: string | string[] }) =>
    this.postRequest<IResponseApi<ISuccessData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_REMOVE_BOT,
      params,
    );

  /**
   * Đẩy kịch bản lên Meta (Facebook).
   * V2: POST /chatbot/script/pushscripttometa
   */
  sendScriptToMeta = (params: ISendScriptToMetaParams) =>
    this.postRequest<IResponseApi<ISendScriptToMetaData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_PUSH_TO_META,
      params,
    );

  // ───────────────────────────── BLOCK ─────────────────────────────

  /**
   * Thêm mới block (kịch bản).
   * V2: POST /chatbot/script/addblock
   */
  addBlock = (params: IAddBlockParams) =>
    this.postRequest<IResponseApi<IItemData<BotBlock>>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_ADD_BLOCK,
      params,
    );

  /**
   * Cập nhật block (tên, alias...).
   * V2: POST /chatbot/script/updateblock
   */
  updateBlock = (params: { id: string; name?: string; alias?: string }) =>
    this.postRequest<IResponseApi<ISuccessData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_UPDATE_BLOCK,
      params,
    );

  /**
   * Xoá 1 block khỏi bot.
   * V2: POST /chatbot/script/removeblock
   */
  removeBlock = (params: { id: string; botId: string }) =>
    this.postRequest<IResponseApi<ISuccessData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_REMOVE_BLOCK,
      params,
    );

  // ───────────────────────────── CARD ─────────────────────────────

  /**
   * Thêm mới card (tin nhắn trong block).
   * V2: POST /chatbot/script/addcard
   */
  addCard = (params: IAddCardParams) =>
    this.postRequest<IResponseApi<IItemData<BotCard>>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_ADD_CARD,
      params,
    );

  /**
   * Cập nhật card.
   * V2: POST /chatbot/script/updatecard
   */
  updateCard = (params: IUpdateCardParams) =>
    this.postRequest<IResponseApi<IUpdateCardData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_UPDATE_CARD,
      params,
    );

  /**
   * Xoá card.
   * V2: POST /chatbot/script/removecard
   */
  removeCard = (params: { id: string }) =>
    this.postRequest<IResponseApi<ISuccessData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_REMOVE_CARD,
      params,
    );

  // ───────────────────────────── GROUP ─────────────────────────────

  /**
   * Thêm mới group (nhóm kịch bản / từ khóa).
   * V2: POST /chatbot/script/addgroup
   */
  addGroup = (params: IAddGroupParams) =>
    this.postRequest<IResponseApi<IItemData<BotGroup>>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_ADD_GROUP,
      params,
    );

  /**
   * Cập nhật group.
   * V2: POST /chatbot/script/updategroup
   */
  updateGroup = (params: { id: string; name?: string; order?: number }) =>
    this.postRequest<IResponseApi<IItemData<BotGroup>>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_UPDATE_GROUP,
      params,
    );

  /**
   * Lấy danh sách group theo botId.
   * V2: POST /chatbot/script/listgroup
   */
  listGroup = (params: { botId: string; type?: string }) =>
    this.postRequest<IResponseApi<IListGroupData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_LIST_GROUP,
      params,
    );

  /**
   * Clone block/group sang bot khác.
   * V2: POST /chatbot/script/cloneblockgroup
   */
  cloneBlockGroup = (params: ICloneBlockGroupParams) =>
    this.postRequest<IResponseApi<ICloneBlockGroupData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_CLONE_BLOCK_GROUP,
      params,
    );

  // ───────────────────────────── COMMENT ─────────────────────────────

  /**
   * Lấy danh sách comment auto-reply.
   * V2: POST /chatbot/comment/list
   */
  listCommentChatBot = (params: { botId: string }) =>
    this.postRequest<IResponseApi<IListCommentResponse>>(
      uriConsts.chatbot.CHATBOT_COMMENT_LIST,
      params,
    );

  /**
   * Cập nhật comment auto-reply
   * V2: POST /chatbot/comment/update
   */
  updateCommentChatBot = (params: { script: string }) =>
    this.postRequest<IResponseApi<IUpdateCommentResponse>>(
      uriConsts.chatbot.CHATBOT_COMMENT_UPDATE,
      params,
    );

  /**
   * Xoá comment auto-reply.
   * V2: POST /chatbot/comment/remove
   */
  removeCommentChatBot = (params: { id: string; botId?: string }) =>
    this.postRequest<IResponseApi<any>>(
      uriConsts.chatbot.CHATBOT_COMMENT_REMOVE,
      params,
    );

  // ───────────────────────────── CAMPAIGN ─────────────────────────────

  /**
   * Lấy danh sách campaign.
   * V2: POST /chatbot/campaign/list
   */
  listCampaign = (params: IListCampaignParams) =>
    this.postRequest<IResponseApi<IResDataElasticDefault<ICampaignItem[]>>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_LIST,
      params ?? {},
    );

  /**
   * Lấy danh sách user trong campaign.
   * V2: POST /chatbot/campaign/listuser
   */
  listCampaignUser = (params?: IListCampaignUserParams) =>
    this.postRequest<IResponseApi<IResDataElasticDefault<ICampaignUserItem[]>>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_LIST_USER,
      params ?? {},
    );

  /**
   * Xuất danh sách user trong campaign.
   * V2: POST /chatbot/campaign/listuser
   */
  exportCampaignUser = (params: IListCampaignUserParams) =>
    this.postRequest<IResponseApi<IResDataElasticDefault<TCampaignUserExportItem[]>>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_LIST_USER,
      params,
    );

  /**
   * Thêm campaign.
   * V2: POST /chatbot/campaign/add
   */
  addCampaign = (params: { botId: string; name?: string; status?: number | null }) =>
    this.postRequest<IResponseApi<ICampaignItem>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_ADD,
      params,
    );

  /**
   * Cập nhật campaign.
   * V2: POST /chatbot/campaign/update
   */
  updateCampaign = (params: { id: string; name?: string; status?: number | null }) =>
    this.postRequest<IResponseApi<any>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_UPDATE,
      params,
    );

  /**
   * Xoá campaign.
   * V2: POST /chatbot/campaign/remove
   */
  removeCampaign = (params: { id: string }) =>
    this.postRequest<IResponseApi<any>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_REMOVE,
      params,
    );

  /**
   * Lấy log campaign.
   * V2: POST /chatbot/campaign/listlogs
   */
  listCampaignLogs = (params: IListCampaignLogsParams) =>
    this.postRequest<IResponseApi<IResDataElasticDefault<ICampaignLogItem[]>>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_LIST_LOGS,
      params ?? {},
    );

  /**
   * Thêm log campaign.
   * V2: POST /chatbot/campaign/addlog
   */
  addLogCampaign = (params: IAddLogCampaignParams) =>
    this.postRequest<IResponseApi<ICampaignLogItem>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_ADD_LOG,
      params,
    );

  /**
   * Gửi tin nhắn campaign theo từng lô.
   * V2: POST /chatbot/campaign/sendmessage
   */
  sendMessageCampaign = (params: ISendMessageCampaignParams) =>
    this.postRequest<IResponseApi<ISendMessageCampaignData>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_SEND_MESSAGE,
      params,
    );

  /**
   * Cập nhật hàng loạt user trong campaign.
   * V2: POST /chatbot/campaign/updatecampaignuser
   */
  updateCampaignUser = (params: IUpdateCampaignUserParams) =>
    this.postRequest<IResponseApi<any[]>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_UPDATE_USER,
      params,
    );

  /**
   * Đếm số lượng user trong campaign.
   * V2: POST /chatbot/campaign/countuser
   */
  loadCountUserCampaign = (params: { ids: string[] }) =>
    this.postRequest<IResponseApi<ICampaignUserCountData>>(
      uriConsts.chatbot.CHATBOT_CAMPAIGN_COUNT_USER,
      params,
    );

  // ───────────────────────────── MESSAGE ─────────────────────────────

  /**
   * Lấy chi tiết message theo id.
   * V2: POST /chatbot/script/getmessage
   */
  getMessageById = (params: { messageId: string; businessId?: string }) =>
    this.postRequest<IResponseApi<IScriptMessageItem>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_GET_MESSAGE,
      params,
    );

  /**
   * Thêm message.
   * V2: POST /chatbot/script/addmessage
   */
  addMessage = (params: { businessId: string; message: string }) =>
    this.postRequest<IResponseApi<IItemData<IScriptMessageItem>>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_ADD_MESSAGE,
      params,
    );

  /**
   * Cập nhật message.
   * V2: POST /chatbot/script/updatemessage
   */
  updateMessage = (params: { messageId: string; businessId: string; message: string }) =>
    this.postRequest<IResponseApi<IUpdateMessageData>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_UPDATE_MESSAGE,
      params,
    );

  // ───────────────────────────── EXTERNAL ─────────────────────────────

  /**
   * Lấy danh sách bài viết của page.
   * V2: POST /setting/manageposts/index
   * Thay cho v1 POST /chat/facebook/postmanage
   */
  getManagePosts = (params: IPostManageParams) =>
    this.postRequest<IResponseApi<{ result: IPostManageItem[]; next: boolean | string | null }>>(
      uriConsts.settings.SETTING_MANAGE_POSTS,
      params,
    );

  /**
   * Lấy danh sách assistant AI.
   * POST /genai/assistantlist
   */
  getAssistantList = (params: { businessId: string; limit?: number }) =>
    this.postRequest<IResponseApi<any>>(uriConsts.genai.ASSISTANT_LIST, params);

  /**
   * Lấy danh sách mã giảm giá (voucher) Shopee.
   * POST /chat/shopee/getvoucherlist
   */
  getVoucherList = (params: { pageId: string }) =>
    this.postRequest<IResponseApi<IGetVoucherListResponse>>(
      uriConsts.chat.SHOPEE_GET_VOUCHER_LIST,
      params,
    );

  /**
   * Gửi test request cho card API (gọi thử API bên thứ 3 đã cấu hình).
   * V2: POST /chatbot/script/scriptapitest
   */
  scriptApiTest = (params: { settings: string }) =>
    this.postRequest<IResponseApi<IScriptApiTestResponse>>(
      uriConsts.chatbot.CHATBOT_SCRIPT_API_TEST,
      params,
    );

  /**
   * Lấy thông tin khách hàng theo pageId + fanpageUserId.
   * POST /chat/customer/list
   */
  getCustomers = (params: { pageIds: string[]; fanpageUserId: string[] }) =>
    this.postRequest<IResponseApi<any>>(uriConsts.chat.CUSTOMER_LIST, params);
}
